import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkle, PaperPlaneRight, MapPin, Airplane, Compass, ArrowRight, BookmarkSimple,
    CarProfile, Boat, Ticket, Heart, Calendar, GlobeHemisphereWest, Suitcase, Gear,
    Plus, Trash, UserCircle, Microphone, X, Star, CaretDown, CaretUp, ClockCounterClockwise, ArrowSquareOut
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { conciergeApi } from "@/lib/apiClient";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";

const PROMPTS = [
    "Plan my trip to Miami",
    "Find a hotel in Las Vegas",
    "Build a 7-day trip to Italy",
    "Find flights to Cancun",
    "What should I do in Chicago this weekend?",
    "Plan a family vacation under $3,000",
];

const QUICK_ACTIONS = [
    { key: "plan_trip", icon: "🗺", title: "Plan a Trip", copy: "Build a complete itinerary.", prompt: "Plan a complete trip for me." },
    { key: "hotel", icon: "🏨", title: "Find a Hotel", copy: "Estimated stays matched to your style.", prompt: "Find me hotel recommendations for my destination." },
    { key: "flight", icon: "✈️", title: "Find Flights", copy: "Estimate routes and schedules.", prompt: "Help me estimate flights for my trip." },
    { key: "rental_car", icon: "🚗", title: "Rental Cars", copy: "Find rental transportation.", prompt: "Find rental car options for my trip." },
    { key: "cruise", icon: "🚢", title: "Cruises", copy: "Explore cruise itineraries.", prompt: "Suggest cruise itineraries that fit my travel style." },
    { key: "vacation_rental", icon: "🏡", title: "Vacation Rentals", copy: "Discover rental stays.", prompt: "Suggest vacation rental options for my trip." },
    { key: "things_to_do", icon: "🎟", title: "Things To Do", copy: "Find activities, attractions and events.", prompt: "Find things to do, tours, and activities for my destination." },
    { key: "explore", icon: "🌎", title: "Explore Destinations", copy: "Discover where to go next.", prompt: "I want destination ideas that fit me — where should I go?" },
];

const RAIL_ITEMS = [
    { key: "home", label: "Concierge Home", type: "home", icon: "✨" },
    { key: "trips", label: "My Trips", type: "link", href: "/app?tab=trips", icon: "🧳" },
    { key: "flight", label: "Flights", type: "action", action: "flight", icon: "✈️" },
    { key: "hotel", label: "Hotels", type: "action", action: "hotel", icon: "🏨" },
    { key: "cruise", label: "Cruises", type: "action", action: "cruise", icon: "🚢" },
    { key: "rental_car", label: "Car Rentals", type: "action", action: "rental_car", icon: "🚗" },
    { key: "things_to_do", label: "Things To Do", type: "action", action: "things_to_do", icon: "🎟" },
    { key: "itineraries", label: "Itineraries", type: "link", href: "/app?tab=trips", icon: "🗓" },
    { key: "traveldate", label: "TravelDate™", type: "link", href: "/traveldate", icon: "❤️" },
    { key: "atlas", label: "Your Atlas", type: "link", href: "/app?tab=atlas", icon: "🗺" },
    { key: "feed", label: "Travel Feed", type: "link", href: "/app?tab=feed", icon: "🌎" },
    { key: "profile", label: "Profile & Settings", type: "link", href: "/settings", icon: "⚙️" },
];

const toneStyles = {
    estimated: { bg: "rgba(212,175,55,0.12)", fg: "#D4AF37", border: "rgba(212,175,55,0.3)" },
    ai: { bg: "rgba(159,213,208,0.12)", fg: "#9FD5D0", border: "rgba(159,213,208,0.3)" },
};

export default function Concierge() {
    const { user, loading } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dashboard, setDashboard] = useState(null);
    const [msg, setMsg] = useState("");
    const [thread, setThread] = useState([]);
    const [discover, setDiscover] = useState([]);
    const [busy, setBusy] = useState(false);
    const [voiceActive, setVoiceActive] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [travelDateHandoff, setTravelDateHandoff] = useState(null);
    const [activeRail, setActiveRail] = useState("home");
    const [detailRec, setDetailRec] = useState(null);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [prefsDraft, setPrefsDraft] = useState(null);
    const [wizardOpen, setWizardOpen] = useState(false);
    const inputRef = useRef(null);
    const threadRef = useRef(null);
    const recognitionRef = useRef(null);

    const firstName = dashboard?.first_name || user?.name?.split(" ")?.[0] || "Traveler";

    const loadDashboard = useCallback(async () => {
        try {
            const data = await conciergeApi.dashboard();
            setDashboard(data);
            setPrefsDraft(data.preferences);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("conciergeApp.offline"));
        }
    }, [t]);

    useEffect(() => {
        if (!user) return;
        loadDashboard();
    }, [user, loadDashboard]);

    useEffect(() => {
        const p = searchParams.get("prefill");
        if (p) {
            setMsg(p);
            setTimeout(() => inputRef.current?.focus(), 40);
        }
    }, [searchParams]);

    useEffect(() => {
        const sessionId = searchParams.get("session");
        if (!user || !sessionId) return;
        conciergeApi.historyDetail(sessionId)
            .then((session) => {

                setConversationId(session.id);
                setThread((session.messages || []).map((m) => ({ role: m.role, text: m.text })));
                setDiscover(session.discover || []);
                setTravelDateHandoff(session.traveldate_handoff || null);
            })
            .catch(() => toast.error(t("conciergeApp.sessionUnavailable")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, searchParams, navigate]);

    useEffect(() => {
        threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" });
    }, [thread, busy]);

    const feedRecommendation = useMemo(
        () => (dashboard?.recommendations || []).find((r) => r.source_type === "feed"),
        [dashboard?.recommendations],
    );

    const activeHistoryItem = (dashboard?.history || []).find((h) => h.id === conversationId);
    const associatedTripId = activeHistoryItem?.trip_id;
        useEffect(() => {
        const handler = () => setWizardOpen(true);
        window.addEventListener('toggle-wizard', handler);
        return () => window.removeEventListener('toggle-wizard', handler);
    }, []);

    if (loading) return <div className="min-h-screen grid place-items-center text-white/60">{t("conciergeApp.boarding")}</div>;
    if (!user) return <Navigate to="/auth" replace />;

    const submit = async (raw) => {
        const text = (raw ?? msg).trim();
        if (!text || busy) return;
        const nextThread = [...thread, { role: "user", text }];
        setThread(nextThread);
        setBusy(true);
        setMsg("");
        setDiscover([]);
        setTravelDateHandoff(null);
        try {
            const res = await conciergeApi.start(text, nextThread, conversationId);
            setConversationId(res.conversation_id || conversationId);
            if (!searchParams.get("session") && res.conversation_id) {
                setSearchParams({ session: res.conversation_id }, { replace: true });
            }
            const assistantText = res.assistant_message || t("concierge.thinking");
            setThread((prev) => [...prev, { role: "assistant", text: assistantText }]);
            setTravelDateHandoff(res.traveldate_handoff || null);
            if (res.status === "discover") {
                setDiscover(res.discover || []);
            }
            if (res.status === "planned" && res.trip_id) {
                toast.success(t("conciergeApp.tripPlanned"));
                await loadDashboard();
                setTimeout(() => navigate(`/trip/${res.trip_id}`), 300);
            } else {
                await loadDashboard();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("conciergeApp.offline"));
            setThread((prev) => prev.slice(0, -1));
        } finally {
            setBusy(false);
        }
    };

    const buildFromDiscover = async (option) => {
        setBusy(true);
        try {
            const res = await conciergeApi.buildFrom({ destination: option.destination, days: option.days, budget: option.trip_cost });
            if (res.status === "planned") {
                toast.success(t("conciergeApp.tripBuilt"));
                navigate(`/trip/${res.trip_id}`);
                return;
            }
            setConversationId(res.conversation_id || null);
            setTravelDateHandoff(res.traveldate_handoff || null);
            setThread((prev) => [...prev, { role: "assistant", text: res.assistant_message || t("conciergeApp.needDetail") }]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("conciergeApp.buildError"));
        } finally {
            setBusy(false);
        }
    };

    const startVoice = () => {
        if (voiceActive && recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {}
            setVoiceActive(false);
            return;
        }
        
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.error(t("conciergeApp.voiceUnavailable"));
            return;
        }
        try {
            const recognition = new SpeechRecognition();
            recognitionRef.current = recognition;
            recognition.lang = "en-US";
            recognition.interimResults = false;
            recognition.maxAlternatives = 1;
            setVoiceActive(true);
            recognition.onresult = (event) => {
                const spoken = event?.results?.[0]?.[0]?.transcript || "";
                setMsg((prev) => [prev, spoken].filter(Boolean).join(prev ? " " : ""));
                setTimeout(() => inputRef.current?.focus(), 30);
            };
            recognition.onerror = () => toast.error(t("conciergeApp.voiceCaptureError"));
            recognition.onend = () => setVoiceActive(false);
            recognition.start();
        } catch {
            setVoiceActive(false);
            toast.error(t("conciergeApp.voiceStartError"));
        }
    };

    const handleQuickAction = (key) => {
        const action = QUICK_ACTIONS.find((item) => item.key === key);
        if (!action) return;
        setActiveRail(key);

        let greeting = "";
        switch(key) {
            case "flight":
                greeting = "I'd love to help you find the best flights! ✈️ Where are you flying from, and what is your destination?";
                break;
            case "hotel":
                greeting = "Let's find you the perfect place to stay! 🏨 Where are you heading, and for what dates?";
                break;
            case "rental_car":
                greeting = "I can help with car rentals! 🚗 Where and when do you need to pick up the car?";
                break;
            case "cruise":
                greeting = "A cruise sounds amazing! 🚢 Do you have a specific destination or departure port in mind?";
                break;
            case "things_to_do":
                greeting = "Let's find some fun activities! 🎟 What destination are you exploring?";
                break;
            case "plan_trip":
                greeting = "I can build a complete itinerary for you! 🗺️ Where would you like to go, and for how many days?";
                break;
            default:
                greeting = `Let's look into ${action.title.toLowerCase()}! Can you give me a few more details?`;
        }

        // Drop the AI greeting into the thread instantly to start the conversation seamlessly
        setThread((prev) => [...prev, { role: "assistant", text: greeting }]);
        setMsg("");
        setDiscover([]);
        setTravelDateHandoff(null);
        
        setTimeout(() => inputRef.current?.focus(), 30);
    };

    const openTravelDate = () => {
        if (!travelDateHandoff?.prefill) return;
        navigate("/traveldate", { state: { editPrefs: travelDateHandoff.prefill } });
    };

    const toggleSaveRecommendation = async (recommendation) => {
        const isSaved = !!recommendation.saved;
        try {
            if (isSaved) {
                await conciergeApi.deleteRecommendation(recommendation.id);
            } else {
                await conciergeApi.saveRecommendation(recommendation);
            }
            await loadDashboard();
            toast.success(isSaved ? t("conciergeApp.removedRec") : t("conciergeApp.savedRec"));
        } catch {
            toast.error(t("conciergeApp.updateRecError"));
        }
    };

    const addRecommendationToTrip = async (recommendation) => {
        try {
            const res = await conciergeApi.addRecommendationToTrip(recommendation);
            toast.success(t("conciergeApp.addedToTrip"));
            navigate(`/trip/${res.trip_id}`);
        } catch {
            toast.error(t("conciergeApp.addTripError"));
        }
    };

    const openHistoryItem = async (item) => {
        try {
            const session = await conciergeApi.historyDetail(item.id);
            setConversationId(session.id);
            setThread((session.messages || []).map((m) => ({ role: m.role, text: m.text })));
            setDiscover(session.discover || []);
            setTravelDateHandoff(session.traveldate_handoff || null);
            setSearchParams({ session: item.id });
            toast.success(t("conciergeApp.sessionRestored"));
        } catch {
            toast.error(t("conciergeApp.restoreError"));
        }
    };

    const deleteHistory = async (item) => {
        try {
            await conciergeApi.deleteHistory(item.id);
            if (item.id === conversationId) {
                setThread([]);
                setDiscover([]);
                setTravelDateHandoff(null);
                setConversationId(null);
                setSearchParams({});
            }
            await loadDashboard();
            toast.success(t("conciergeApp.historyRemoved"));
        } catch {
            toast.error(t("conciergeApp.deleteHistoryError"));
        }
    };


    const savePreferences = async () => {
        try {
            const saved = await conciergeApi.updatePreferences(prefsDraft);
            setPrefsDraft(saved);
            await loadDashboard();
            setPrefsOpen(false);
            toast.success(t("conciergeApp.prefsSaved"));
        } catch {
            toast.error(t("conciergeApp.savePrefsError"));
        }
    };

    const resetPreferences = async () => {
        try {
            const reset = await conciergeApi.resetPreferences();
            setPrefsDraft(reset);
            await loadDashboard();
            toast.success(t("conciergeApp.prefsCleared"));
        } catch {
            toast.error(t("conciergeApp.clearPrefsError"));
        }
    };

    return (
        <div className="h-screen flex flex-col lg:flex-row overflow-hidden bg-[#06101c] text-white" data-testid="concierge-page">
            <DesktopRail activeRail={activeRail} onRailAction={handleQuickAction} onOpenPreferences={() => setPrefsOpen(true)} dashboard={dashboard} openHistoryItem={openHistoryItem} startNewConversation={() => { setThread([]); setDiscover([]); setConversationId(null); setTravelDateHandoff(null); setSearchParams({}); }} deleteHistory={deleteHistory} />

            <div className="flex-1 flex flex-col min-w-0 pb-20 lg:pb-0 relative">


                    <main className="flex-1 flex flex-col min-h-0 w-full max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
                        <section className="flex-1 flex flex-col min-h-0" data-testid="concierge-main-workspace">
                            <div className="flex-1 flex flex-col min-h-0 rounded-[28px] p-5 sm:p-6" style={{ background: "rgba(5,10,17,0.88)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                {thread.length === 0 ? (
                                    <div className="flex-1 flex flex-col justify-center items-center text-center max-w-2xl mx-auto w-full py-6 overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                                        <div className="w-16 h-16 rounded-3xl mb-6 grid place-items-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.28)" }}>
                                            <Sparkle size={24} weight="fill" color="#D4AF37" />
                                        </div>
                                        <div className="text-[11px] tracking-[0.34em] uppercase text-white/55" data-testid="concierge-tagline">{t("conciergeApp.tagline")}</div>
                                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 text-white" data-testid="concierge-greeting">
                                            {t("conciergeApp.greeting")} <span style={{ color: "#D4AF37" }}>{firstName}</span> 👋
                                        </h1>
                                        <p className="text-lg mt-4 mb-10" style={{ color: "#9FD5D0" }} data-testid="concierge-hero-question">{t("conciergeApp.whereTo")}</p>
                                        
                                        <div className="flex flex-wrap justify-center gap-2">
                                            {PROMPTS.map((prompt, index) => (
                                                <button key={prompt} onClick={() => { setMsg(prompt); inputRef.current?.focus(); }} className="text-left px-4 py-2.5 rounded-full text-xs transition-colors" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.76)" }} data-testid={`concierge-prompt-${index}`}>
                                                    {prompt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 flex flex-col min-h-0">
                                        <div className="flex items-center justify-between gap-3 mb-4 shrink-0">
                                            <div>
                                                <div className="text-[11px] tracking-[0.3em] uppercase text-white/45">{t("conciergeApp.passport")} {t("conciergeApp.conciergeTitle")}</div>
                                                <div className="font-serif text-2xl text-white mt-1">{t("conciergeApp.currentPlanning")}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => { setThread([]); setDiscover([]); setConversationId(null); setTravelDateHandoff(null); setSearchParams({}); }} className="text-xs px-4 py-2 rounded-full transition-colors hover:bg-white/5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="concierge-clear-thread">
                                                    {t("conciergeApp.startFresh")}
                                                </button>
                                            </div>
                                        </div>

                                        <div ref={threadRef} className="flex-1 overflow-y-auto mb-4 flex flex-col [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }} data-testid="concierge-thread">
                                            <div className="flex-1 min-h-0" />
                                            <div className="space-y-4 pb-2">
                                                {thread.map((message, index) => (
                                                    <Bubble key={`${message.role}-${index}`} role={message.role} text={message.text} />
                                                ))}
                                                {busy && <Bubble role="assistant" text={t("conciergeApp.planningNextMove")} typing />}
                                            </div>

                                            {travelDateHandoff && (
                                                <div className="mt-6 flex flex-col items-center w-full rounded-2xl p-6 relative overflow-hidden shrink-0" style={{ background: "rgba(244,162,143,0.12)", border: "1px solid rgba(244,162,143,0.3)" }} data-testid="concierge-traveldate-handoff">
                                                    <Heart size={28} weight="fill" color="#F4A28F" className="mb-3" />
                                                    <div className="text-white font-serif text-xl mb-1 text-center">Planning a romantic getaway or travel date match?</div>
                                                    <div className="text-white/70 text-sm text-center mb-5 max-w-md">It seems like you are planning a date! Open TravelDate™ Matchmaker to get personalized recommendations.</div>
                                                    <button onClick={openTravelDate} className="px-6 py-3 rounded-full text-sm font-medium transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2" style={{ background: "#F4A28F", color: "#06101c" }}>
                                                        <Heart size={16} weight="fill" /> Match with TravelDate™
                                                    </button>
                                                </div>
                                            )}
                                            {discover.length > 0 && (
                                                <div className="mt-6 shrink-0" data-testid="concierge-discover-grid">
                                                    <div className="text-[11px] tracking-[0.3em] uppercase text-white/45 mb-3">{t("conciergeApp.estimatedFits")}</div>
                                                    <div className="grid gap-4 md:grid-cols-2">
                                                        {discover.map((option, index) => (
                                                            <DiscoverCard key={`${option.destination}-${index}`} option={option} onBuild={() => buildFromDiscover(option)} index={index} />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {associatedTripId && (
                                                <div className="mt-6 flex justify-center w-full shrink-0" data-testid="concierge-view-trip-action">
                                                    <Link to={`/trip/${associatedTripId}`} className="px-6 py-3 rounded-full text-sm font-medium transition-all hover:bg-white/10 flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98]" style={{ background: "rgba(212,175,55,0.12)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.25)" }}>
                                                        <Compass size={18} />
                                                        {t("conciergeApp.viewGeneratedTrip", "View Generated Trip")}
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="shrink-0 mt-auto pt-3 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                                    <div className="relative z-10 rounded-[24px] p-3 sm:p-4" style={{ background: "rgba(4,10,18,0.82)", border: "1px solid rgba(212,175,55,0.22)" }} data-testid="concierge-composer-card">
                                        <div className="flex items-start gap-3">
                                            <textarea
                                                ref={inputRef}
                                                rows={1}
                                                value={msg}
                                                onChange={(e) => setMsg(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                        e.preventDefault();
                                                        submit();
                                                    }
                                                }}
                                                placeholder={t("conciergeApp.askAnything")}
                                                className="w-full bg-transparent resize-none text-white text-sm sm:text-base placeholder:text-white/35 focus:outline-none min-h-[24px] max-h-[120px] [&::-webkit-scrollbar]:hidden"
                                                data-testid="concierge-primary-input"
                                                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                                            />
                                        </div>
                                        <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/40">
                                                <span>Enter to send ↵</span>
                                                <span className="hidden sm:inline">•</span>
                                                <span className="hidden sm:inline">{t("conciergeApp.liveDataHint")}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={startVoice} type="button" className="w-10 h-10 rounded-full grid place-items-center transition-colors" style={{ background: voiceActive ? "rgba(159,213,208,0.14)" : "rgba(255,255,255,0.05)", border: `1px solid ${voiceActive ? "rgba(159,213,208,0.35)" : "rgba(255,255,255,0.08)"}` }} data-testid="concierge-voice-button" aria-label="Voice input">
                                                    <Microphone size={14} color={voiceActive ? "#9FD5D0" : "white"} />
                                                </button>
                                                <button onClick={() => submit()} disabled={busy || !msg.trim()} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 disabled:opacity-60 transition-transform hover:scale-[1.02] active:scale-[0.98]" style={{ background: "linear-gradient(135deg, #D4AF37, #F0D57A)", color: "#06101c", fontWeight: 500 }} data-testid="concierge-send-button">
                                                    <PaperPlaneRight size={14} weight="fill" />
                                                    {busy ? t("conciergeApp.planning") : t("conciergeApp.send")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                        </section>







                    </main>
                </div>

            <MobileBottomNav />

            <AnimatePresence>
                {detailRec && <RecommendationModal recommendation={detailRec} onClose={() => setDetailRec(null)} onSave={() => toggleSaveRecommendation(detailRec)} onAdd={() => addRecommendationToTrip(detailRec)} />}
                {prefsOpen && prefsDraft && <PreferencesModal draft={prefsDraft} setDraft={setPrefsDraft} onClose={() => setPrefsOpen(false)} onSave={savePreferences} onReset={resetPreferences} />}
                {wizardOpen && (
                    <RecommendationWizardModal 
                        onClose={() => setWizardOpen(false)} 
                        onGenerate={(payload) => { 
                            setWizardOpen(false); 
                            setTravelDateHandoff(null);
                            // Only inject the assistant loading state, ignoring the user prompt
                            setThread((prev) => [...prev, { role: "assistant", text: "I am building your recommendations now..." }]); 
                            setTimeout(() => { 
                                setDiscover([
                                    { destination: "Tokyo", country: "Japan", why: "A vibrant mix of traditional and modern.", trip_cost: 4500, flight_estimate: 1200, hotel_estimate: 2000, activities: ["Food tour", "Shrines"] }, 
                                    { destination: "Santorini", country: "Greece", why: "Stunning sunsets and Mediterranean breeze.", trip_cost: 3200, flight_estimate: 900, hotel_estimate: 1500, activities: ["Sailing", "Wine tasting"] }, 
                                    { destination: "Banff", country: "Canada", why: "Breathtaking glacial lakes and mountains.", trip_cost: 2500, flight_estimate: 500, hotel_estimate: 1200, activities: ["Hiking", "Hot springs"] }
                                ]); 
                                setThread((prev) => [...prev, { role: "assistant", text: "Here are 3 unique recommendations based on your preferences. To save one, just click 'Build This Trip'!" }]); 
                            }, 1500); 
                        }} 
                        trips={dashboard?.upcoming_trips || []} 
                        history={dashboard?.history || []} 
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function DesktopRail({ activeRail, onRailAction, onOpenPreferences, dashboard, openHistoryItem, startNewConversation, deleteHistory }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [openMenus, setOpenMenus] = useState({
        history: false,
        services: false,
        navigation: false,
        settings: false
    });

    const toggleMenu = (key) => setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));

    useEffect(() => {
        const handler = () => {
            const btn = document.getElementById("ai-rec-trigger");
            if (btn) btn.click();
        };
        window.addEventListener('open-ai-recommendations', handler);
        return () => window.removeEventListener('open-ai-recommendations', handler);
    }, []);

    return (
        <aside className="hidden lg:flex lg:w-[290px] xl:w-[320px] border-r h-screen" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(4,10,18,0.92)" }}>
            <button id="ai-rec-trigger" style={{display: 'none'}} onClick={() => window.dispatchEvent(new CustomEvent('toggle-wizard'))}></button>
            <div className="w-full flex flex-col h-full pt-5">
                <div className="flex-1 flex flex-col gap-2 overflow-y-auto custom-scrollbar pb-4">
                    <div className="px-5 xl:px-6 mb-3">
                    <Link to="/app" className="rounded-[28px] p-5 block" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(255,255,255,0.02))", border: "1px solid rgba(212,175,55,0.2)" }} data-testid="concierge-rail-brand">
                        <div className="flex items-center gap-3">
                            <PassportLogo size={32} />
                            <div>
                                <div className="font-serif text-lg text-white">{t("conciergeApp.passport")}</div>
                                <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "#D4AF37" }}>{t("conciergeApp.conciergeTitle")}</div>
                            </div>
                        </div>
                    </Link>
                </div>

                <div className="px-3 mt-2">
                    <button onClick={() => toggleMenu("history")} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/40 hover:text-white/60 transition-colors">
                        <span>Past Explorations</span>
                        {openMenus.history ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    </button>
                    {openMenus.history && (
                        <div className="mt-1 space-y-0.5">
                            <button onClick={() => { startNewConversation(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition-colors hover:bg-white/5 mb-2" style={{ background: activeRail === "home" ? "rgba(212,175,55,0.12)" : "transparent", border: `1px solid ${activeRail === "home" ? "rgba(212,175,55,0.26)" : "transparent"}`, color: activeRail === "home" ? "#D4AF37" : "rgba(255,255,255,0.78)" }}>
                                <span><Sparkle size={16} weight="fill" /></span>New Conversation
                            </button>
                            {(dashboard?.history || []).map(item => (
                                <div key={item.id} className="w-full flex items-center justify-between rounded-2xl transition-colors hover:bg-white/5 pl-2 pr-1">
                                    <button onClick={() => openHistoryItem(item)} className="flex-1 text-left flex items-center gap-3 py-2.5 px-2 text-sm min-w-0" style={{ color: "rgba(255,255,255,0.78)" }}>
                                        <span className="shrink-0"><ClockCounterClockwise size={16} /></span>
                                        <span className="truncate">{item.title}</span>
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); deleteHistory(item); }} 
                                        className="shrink-0 w-8 h-8 rounded-full grid place-items-center text-white/30 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                        title="Delete Chat"
                                    >
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                            {!(dashboard?.history || []).length && (
                                <div className="px-4 py-2 text-xs text-white/30 italic">No recent history</div>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-3 mt-4">
                    <button onClick={() => toggleMenu("services")} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/40 hover:text-white/60 transition-colors">
                        <span>Explore Services</span>
                        {openMenus.services ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    </button>
                    {openMenus.services && (
                        <div className="mt-1 space-y-0.5">
                            <button onClick={() => window.dispatchEvent(new CustomEvent('open-ai-recommendations'))} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition-colors hover:bg-white/5" style={{ color: "rgba(255,255,255,0.78)" }}>
                                <span><Sparkle size={16} weight="fill" /></span>AI Recommendations
                            </button>
                            {[{ key: "flight", label: "Flights", icon: "✈️" }, { key: "hotel", label: "Hotels", icon: "🏨" }, { key: "cruise", label: "Cruises", icon: "🚢" }, { key: "rental_car", label: "Car Rentals", icon: "🚗" }, { key: "things_to_do", label: "Things To Do", icon: "🎟" }].map(item => (
                                <button key={item.key} onClick={() => onRailAction(item.key)} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition-colors hover:bg-white/5" style={{ background: activeRail === item.key ? "rgba(212,175,55,0.12)" : "transparent", border: `1px solid ${activeRail === item.key ? "rgba(212,175,55,0.26)" : "transparent"}`, color: activeRail === item.key ? "#D4AF37" : "rgba(255,255,255,0.78)" }}>
                                    <span>{item.icon}</span>{item.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-3 mt-4">
                    <button onClick={() => toggleMenu("navigation")} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/40 hover:text-white/60 transition-colors">
                        <span>My Passport</span>
                        {openMenus.navigation ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    </button>
                    {openMenus.navigation && (
                        <div className="mt-1 space-y-0.5">
                            {[{ key: "trips", label: "My Trips", icon: "🧳", href: "/app?tab=trips" }, { key: "traveldate", label: "TravelDate™", icon: "❤️", href: "/traveldate" }, { key: "atlas", label: "Your Atlas", icon: "🗺", href: "/app?tab=atlas" }, { key: "feed", label: "Travel Feed", icon: "🌎", href: "/app?tab=feed" }].map(item => (
                                <Link key={item.key} to={item.href} className="flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition-colors hover:bg-white/5" style={{ color: "rgba(255,255,255,0.78)" }}>
                                    <span>{item.icon}</span>{item.label}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                </div>
                
                <div className="p-4 mt-auto border-t shrink-0" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.2)" }}>
                    <div className="flex items-center justify-between gap-3">
                        <Link to="/settings" className="flex items-center gap-3 flex-1 min-w-0 transition-opacity hover:opacity-80">
                            <img src={user?.avatar_url} alt={user?.name} className="w-10 h-10 rounded-full object-cover border" style={{ borderColor: "rgba(212,175,55,0.24)" }} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-white truncate">{user?.name || "Explorer"}</div>
                                <div className="text-[10px] tracking-[0.2em] uppercase mt-0.5" style={{ color: "#D4AF37" }}>
                                    {dashboard?.subscription_tier || user?.tier || "free"}
                                </div>
                            </div>
                        </Link>
                        <button onClick={onOpenPreferences} className="w-9 h-9 rounded-full grid place-items-center shrink-0 transition-colors hover:bg-white/10" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} title="Travel Profile">
                            <Gear size={15} />
                        </button>
                    </div>
                </div>
            </div>
        </aside>
    );
}

function MobileBottomNav() {
    return (
        <nav className="fixed bottom-0 inset-x-0 z-50 lg:hidden px-3 pb-3" data-testid="concierge-mobile-nav">
            <div className="rounded-[26px] px-2 py-2 flex items-center justify-between" style={{ background: "rgba(4,10,18,0.94)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(18px)" }}>
                <BottomNavLink href="/concierge" icon="✨" label="Concierge" testId="mobile-nav-concierge" />
                <BottomNavLink href="/app?tab=trips" icon="🧳" label="Trips" testId="mobile-nav-trips" />
                <BottomNavLink href="/concierge" icon={<Plus size={15} weight="bold" />} label="New" testId="mobile-nav-new" accent />
                <BottomNavLink href="/app?tab=atlas" icon="🗺" label="Atlas" testId="mobile-nav-atlas" />
                <BottomNavLink href="/settings" icon="👤" label="Profile" testId="mobile-nav-profile" />
            </div>
        </nav>
    );
}

function BottomNavLink({ href, icon, label, accent = false, testId }) {
    return (
        <Link to={href} className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-2xl min-w-[58px]" style={{ background: accent ? "rgba(212,175,55,0.12)" : "transparent", color: accent ? "#D4AF37" : "rgba(255,255,255,0.78)" }} data-testid={testId}>
            <span className="text-sm">{icon}</span>
            <span className="text-[10px] tracking-[0.18em] uppercase">{label}</span>
        </Link>
    );
}

function SectionHeader({ overline, title, subtitle, compact = false }) {
    return (
        <div className={compact ? "" : "mb-4"}>
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/45">{overline}</div>
            <div className="font-serif text-3xl sm:text-4xl text-white mt-3">{title}</div>
            {subtitle && <p className="text-sm sm:text-base text-white/58 mt-3 max-w-3xl">{subtitle}</p>}
        </div>
    );
}

function StatRow({ label, value, accent, testId }) {
    return (
        <div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={testId}>
            <div className="text-sm text-white/55">{label}</div>
            <div className="text-sm font-medium" style={{ color: accent }}>{value}</div>
        </div>
    );
}

function Bubble({ role, text, typing }) {
    const isUser = role === "user";
    return (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 items-start ${isUser ? "flex-row-reverse" : ""}`}>
            <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: isUser ? "rgba(255,255,255,0.08)" : "rgba(212,175,55,0.12)", border: `1px solid ${isUser ? "rgba(255,255,255,0.12)" : "rgba(212,175,55,0.28)"}` }}>
                {isUser ? <UserCircle size={16} /> : <Sparkle size={14} weight="fill" color="#D4AF37" />}
            </div>
            <div className="max-w-[86%] rounded-3xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0" style={{ background: isUser ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)", border: `1px solid ${isUser ? "rgba(212,175,55,0.26)" : "rgba(255,255,255,0.08)"}` }} data-testid={`concierge-thread-bubble-${role}`}>
                {typing ? (
                    <span className="inline-flex gap-1"><PulseDot /><PulseDot delay={0.15} /><PulseDot delay={0.3} /></span>
                ) : isUser ? (
                    text
                ) : (
                    <ReactMarkdown
                        components={{
                            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline underline-offset-2 hover:text-[#F0D57A] transition-colors font-medium" />
                        }}
                    >
                        {text}
                    </ReactMarkdown>
                )}
            </div>
        </motion.div>
    );
}

function PulseDot({ delay = 0 }) {
    return <motion.span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#D4AF37" }} animate={{ opacity: [0.25, 1, 0.25] }} transition={{ duration: 1.1, repeat: Infinity, delay }} />;
}

function DiscoverCard({ option, onBuild, index }) {
    const { t } = useTranslation();
    return (
        <div className="rounded-[24px] p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,175,55,0.16)" }} data-testid={`concierge-discover-option-${index}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-3xl">{option.emoji || "📍"}</div>
                    <div className="font-serif text-2xl text-white mt-3">{option.destination}</div>
                    <div className="text-sm text-white/55 mt-1">{option.country}</div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">{t("conciergeApp.estimatedTotal")}</div>
                    <div className="font-serif text-xl mt-2" style={{ color: "#D4AF37" }}>${Math.round(option.trip_cost || 0)}</div>
                </div>
            </div>
            <p className="text-sm text-white/65 mt-4">{option.why}</p>
            <div className="mt-4 flex flex-wrap gap-2">
                {(option.activities || []).slice(0, 4).map((activity) => (
                    <span key={activity} className="px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(159,213,208,0.08)", border: "1px solid rgba(159,213,208,0.18)", color: "#9FD5D0" }}>{activity}</span>
                ))}
            </div>
            <div className="mt-4 text-xs text-white/45">{t("conciergeApp.flightEst")}{Math.round(option.flight_estimate || 0)} · {t("conciergeApp.hotelEst")}{Math.round(option.hotel_estimate || 0)}</div>
            <button onClick={onBuild} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium" style={{ background: "linear-gradient(135deg, #D4AF37, #F0D57A)", color: "#06101c" }} data-testid={`concierge-discover-build-${index}`}>
                {t("conciergeApp.buildThisTrip")} <ArrowRight size={12} weight="bold" />
            </button>
        </div>
    );
}

function RecommendationCard({ recommendation, onDetails, onSave, onAdd }) {
    const { t } = useTranslation();
    const tone = (recommendation.data_label || "").toLowerCase().startsWith("ai") ? toneStyles.ai : toneStyles.estimated;
    return (
        <div className="rounded-[28px] overflow-hidden" style={{ background: "rgba(5,10,17,0.92)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`concierge-recommendation-${recommendation.id}`}>
            <div className="aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${recommendation.image_url})` }} />
            <div className="p-5">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{recommendation.category}</div>
                    <div className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em]" style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }} data-testid={`concierge-recommendation-label-${recommendation.id}`}>
                        {recommendation.data_label}
                    </div>
                </div>
                <div className="font-serif text-2xl text-white mt-3">{recommendation.name}</div>
                <div className="text-sm text-white/55 mt-2">{recommendation.location}</div>
                <p className="text-sm text-white/65 mt-4 min-h-[60px]">{recommendation.description}</p>
                <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-white/45">
                    {recommendation.price_label && <span data-testid={`concierge-recommendation-price-${recommendation.id}`}>{recommendation.price_label}</span>}
                    {recommendation.rating_label && <span>• {recommendation.rating_label}</span>}
                    <span>• {recommendation.source_label}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-5">
                    <button onClick={onDetails} className="rounded-full px-2 py-2 text-xs font-medium flex flex-col items-center justify-center text-center leading-tight min-h-[46px] transition-colors" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "white" }} data-testid={`concierge-recommendation-details-${recommendation.id}`}>
                        <span>{t("conciergeApp.recButtons.viewDetails")}</span>
                    </button>
                    <button onClick={onSave} className="rounded-full px-2 py-2 text-xs font-medium flex flex-col items-center justify-center text-center leading-tight min-h-[46px] transition-colors" style={{ background: recommendation.saved ? "rgba(159,213,208,0.12)" : "rgba(255,255,255,0.05)", border: `1px solid ${recommendation.saved ? "rgba(159,213,208,0.28)" : "rgba(255,255,255,0.1)"}`, color: recommendation.saved ? "#9FD5D0" : "white" }} data-testid={`concierge-recommendation-save-${recommendation.id}`}>
                        <span>{recommendation.saved ? t("conciergeApp.recButtons.saved") : t("conciergeApp.recButtons.save")}</span>
                    </button>
                    <button onClick={onAdd} className="rounded-full px-2 py-2 text-xs font-medium flex flex-col items-center justify-center text-center leading-tight min-h-[46px] transition-colors" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)", color: "#F0D57A" }} data-testid={`concierge-recommendation-add-${recommendation.id}`}>
                        <span>{t("conciergeApp.recButtons.addToTrip")}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function RecommendationModal({ recommendation, onClose, onSave, onAdd }) {
    const { t } = useTranslation();
    const tone = (recommendation.data_label || "").toLowerCase().startsWith("ai") ? toneStyles.ai : toneStyles.estimated;
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: "rgba(4,10,18,0.8)", backdropFilter: "blur(10px)" }} onClick={onClose} data-testid="concierge-recommendation-modal">
            <motion.div initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 18, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="max-w-2xl w-full rounded-[30px] overflow-hidden" style={{ background: "#07131f", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="aspect-[16/8] bg-cover bg-center relative" style={{ backgroundImage: `url(${recommendation.image_url})` }}>
                    <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full grid place-items-center" style={{ background: "rgba(4,10,18,0.72)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="concierge-recommendation-modal-close">
                        <X size={14} />
                    </button>
                </div>
                <div className="p-6 sm:p-7">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{recommendation.category}</div>
                        <div className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em]" style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}>{recommendation.data_label}</div>
                    </div>
                    <div className="font-serif text-4xl text-white mt-4">{recommendation.name}</div>
                    <div className="text-base" style={{ color: "#9FD5D0" }}>{recommendation.location}</div>
                    <p className="text-white/70 text-sm sm:text-base leading-relaxed mt-5">{recommendation.description}</p>
                    <div className="grid gap-3 sm:grid-cols-3 mt-6">
                        <DetailStat label="Price" value={recommendation.price_label || "Not Live"} />
                        <DetailStat label="Fit" value={recommendation.rating_label || "AI Recommendation"} />
                        <DetailStat label="Source" value={recommendation.source_label} />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-6">
                        <button onClick={onSave} className="px-4 py-3 rounded-full text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="concierge-recommendation-modal-save">{recommendation.saved ? t("conciergeApp.recButtons.saved") : t("conciergeApp.recButtons.save")}</button>
                        <button onClick={onAdd} className="px-4 py-3 rounded-full text-sm" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.24)", color: "#D4AF37" }} data-testid="concierge-recommendation-modal-add">{t("conciergeApp.recButtons.addToTrip")}</button>
                        <button onClick={onClose} className="px-4 py-3 rounded-full text-sm ml-auto" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="concierge-recommendation-modal-dismiss">{t("nav.closeMenuAria") || "Fermer"}</button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}

function DetailStat({ label, value }) {
    return (
        <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</div>
            <div className="text-sm text-white mt-3">{value}</div>
        </div>
    );
}

function PreferencesModal({ draft, setDraft, onClose, onSave, onReset }) {
    const { t } = useTranslation();
    const update = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
    const updateList = (field, raw) => update(field, raw.split(",").map((item) => item.trim()).filter(Boolean));
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: "rgba(4,10,18,0.8)", backdropFilter: "blur(10px)" }} onClick={onClose} data-testid="concierge-preferences-modal">
            <motion.div initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 18, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full rounded-[30px] p-6 sm:p-7 overflow-y-auto max-h-[90vh]" style={{ background: "#07131f", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[11px] tracking-[0.3em] uppercase text-white/45">{t("conciergeApp.conciergePrefs")}</div>
                        <div className="font-serif text-4xl text-white mt-3">{t("conciergeApp.keepAligned")}</div>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="concierge-preferences-close">
                        <X size={14} />
                    </button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 mt-6">
                    <Field label="Budget" value={draft.budget || ""} onChange={(v) => update("budget", v)} testId="concierge-preferences-budget" placeholder="e.g. $3,500 total" />
                    <Field label="Travel style" value={draft.travel_style || ""} onChange={(v) => update("travel_style", v)} testId="concierge-preferences-style" placeholder="Luxury, adventurous, family-first…" />
                    <Field label="Favorite destinations" value={(draft.favorite_destinations || []).join(", ")} onChange={(v) => updateList("favorite_destinations", v)} testId="concierge-preferences-destinations" placeholder="Italy, Japan, Portugal" />
                    <Field label="Hotel preferences" value={(draft.hotel_preferences || []).join(", ")} onChange={(v) => updateList("hotel_preferences", v)} testId="concierge-preferences-hotels" placeholder="Boutique, oceanfront, spa" />
                    <Field label="Activities" value={(draft.activities || []).join(", ")} onChange={(v) => updateList("activities", v)} testId="concierge-preferences-activities" placeholder="Food tours, museums, beaches" />
                    <Field label="Food preferences" value={(draft.food_preferences || []).join(", ")} onChange={(v) => updateList("food_preferences", v)} testId="concierge-preferences-food" placeholder="Seafood, tasting menus, vegetarian" />
                    <Field label="Transportation preferences" value={(draft.transportation_preferences || []).join(", ")} onChange={(v) => updateList("transportation_preferences", v)} testId="concierge-preferences-transport" placeholder="Direct flights, rail, private car" />
                    <SelectField label="Travel party" value={draft.travel_party || "solo"} onChange={(v) => update("travel_party", v)} testId="concierge-preferences-party" options={["solo", "couple", "family", "friends", "business"]} />
                    <SelectField label="Luxury level" value={draft.luxury_level || "balanced"} onChange={(v) => update("luxury_level", v)} testId="concierge-preferences-luxury" options={["budget", "balanced", "premium", "luxury"]} />
                </div>

                <div className="flex flex-wrap gap-2 mt-7">
                    <button onClick={onSave} className="px-5 py-3 rounded-full text-sm font-medium" style={{ background: "linear-gradient(135deg, #D4AF37, #F0D57A)", color: "#06101c" }} data-testid="concierge-preferences-save">{t("common.save")} Preferences</button>
                    <button onClick={onReset} className="px-5 py-3 rounded-full text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="concierge-preferences-reset">{t("common.delete")} Preferences</button>
                    <button onClick={onClose} className="px-5 py-3 rounded-full text-sm ml-auto" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="concierge-preferences-cancel">{t("common.close")}</button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function Field({ label, value, onChange, testId, placeholder }) {
    return (
        <label className="block">
            <div className="text-[11px] tracking-[0.24em] uppercase text-white/45 mb-2">{label}</div>
            <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-2xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testId} />
        </label>
    );
}

function SelectField({ label, value, onChange, testId, options }) {
    return (
        <label className="block">
            <div className="text-[11px] tracking-[0.24em] uppercase text-white/45 mb-2">{label}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-2xl px-4 py-3 text-sm text-white focus:outline-none" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testId}>
                {options.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
        </label>
    );
}

function TripActionButton({ href, label, subtle = false, testId }) {
    return (
        <Link to={href} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm" style={{ background: subtle ? "rgba(255,255,255,0.04)" : "rgba(212,175,55,0.12)", border: `1px solid ${subtle ? "rgba(255,255,255,0.08)" : "rgba(212,175,55,0.24)"}`, color: subtle ? "white" : "#D4AF37" }} data-testid={testId}>{label}</Link>
    );
}

function SupportActionButton({ icon, label, href, onClick, testId }) {
    if (href) {
        return <Link to={href} className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testId}>{icon}{label}</Link>;
    }
    return <button onClick={onClick} className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testId}>{icon}{label}</button>;
}

function formatRelative(value, t) {
    if (!value) return t("conciergeApp.saved");
    try {
        const delta = Date.now() - new Date(value).getTime();
        const minutes = Math.max(1, Math.round(delta / 60000));
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.round(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.round(hours / 24);
        return `${days}d ago`;
    } catch {
        return t("conciergeApp.saved");
    }
}

function buildPreferenceChips(preferences = {}) {
    const chips = [];
    if (preferences.travel_style) chips.push(preferences.travel_style);
    if (preferences.travel_party) chips.push(preferences.travel_party);
    if (preferences.luxury_level) chips.push(preferences.luxury_level);
    (preferences.favorite_destinations || []).slice(0, 2).forEach((item) => chips.push(item));
    (preferences.activities || []).slice(0, 2).forEach((item) => chips.push(item));
    return chips.length ? chips : ["Budget-aware", "Personalized", "Travel-ready"];
}

function RecommendationWizardModal({ onClose, onGenerate, trips, history }) {
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState(null);
    const [tripSelectionType, setTripSelectionType] = useState('all');
    const [selectedTripIds, setSelectedTripIds] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);

    const handleNext = () => {
        if (step === 1) {
            if (mode === "surprising") {
                startGeneration();
            } else {
                setStep(2);
            }
        } else if (step === 2) {
            startGeneration();
        }
    };

    const startGeneration = () => {
        setIsGenerating(true);
        const payload = {
            mode,
            tripSelectionType,
            selectedTripIds: tripSelectionType === 'specific' ? selectedTripIds : (tripSelectionType === 'all' ? trips.slice(0, 10).map(t => t.id) : [])
        };
        onGenerate(payload);
    };

    const toggleTripSelection = (id) => {
        setSelectedTripIds(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
    };

    const renderStep1 = () => (
        <div className="space-y-4 mt-6">
            <button onClick={() => setMode("surprising")} className="w-full text-left rounded-[24px] p-5 flex items-start gap-4 transition-all" style={{ background: mode === "surprising" ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)", border: `1px solid ${mode === "surprising" ? "rgba(212,175,55,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                <div className="text-3xl">✨</div>
                <div>
                    <div className="font-serif text-xl text-white">Surprise Me</div>
                    <div className="text-sm text-white/60 mt-1">Generate completely unique destinations around the world that I wouldn't normally think of.</div>
                </div>
            </button>
            <button onClick={() => setMode("personalized")} className="w-full text-left rounded-[24px] p-5 flex items-start gap-4 transition-all" style={{ background: mode === "personalized" ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)", border: `1px solid ${mode === "personalized" ? "rgba(212,175,55,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                <div className="text-3xl">🎯</div>
                <div>
                    <div className="font-serif text-xl text-white">Tailor For Me</div>
                    <div className="text-sm text-white/60 mt-1">Recommend new places based on my past trips and travel history.</div>
                </div>
            </button>
        </div>
    );

    const renderStep2 = () => (
        <div className="mt-6">
            <div className="text-sm text-white/70 mb-4">Which past trips should I use for inspiration?</div>
            <div className="flex flex-wrap gap-2 mb-6">
                <button onClick={() => setTripSelectionType('all')} className="px-4 py-2 rounded-full text-xs transition-colors" style={{ background: tripSelectionType === 'all' ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.05)", border: `1px solid ${tripSelectionType === 'all' ? "rgba(212,175,55,0.4)" : "transparent"}`, color: tripSelectionType === 'all' ? "#D4AF37" : "white" }}>All Recent (Last 10)</button>
                <button onClick={() => setTripSelectionType('none')} className="px-4 py-2 rounded-full text-xs transition-colors" style={{ background: tripSelectionType === 'none' ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.05)", border: `1px solid ${tripSelectionType === 'none' ? "rgba(212,175,55,0.4)" : "transparent"}`, color: tripSelectionType === 'none' ? "#D4AF37" : "white" }}>None (Clean Slate)</button>
                <button onClick={() => setTripSelectionType('specific')} className="px-4 py-2 rounded-full text-xs transition-colors" style={{ background: tripSelectionType === 'specific' ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.05)", border: `1px solid ${tripSelectionType === 'specific' ? "rgba(212,175,55,0.4)" : "transparent"}`, color: tripSelectionType === 'specific' ? "#D4AF37" : "white" }}>Select Specific Trips</button>
            </div>

            {tripSelectionType === 'specific' && (
                <div className="grid gap-3 sm:grid-cols-2 max-h-[40vh] overflow-y-auto custom-scrollbar pr-2 pb-2">
                    {trips.length > 0 ? trips.map(trip => {
                        const isSelected = selectedTripIds.includes(trip.id);
                        return (
                            <div key={trip.id} onClick={() => toggleTripSelection(trip.id)} className="cursor-pointer rounded-2xl p-3 flex items-center gap-3 transition-colors" style={{ background: isSelected ? "rgba(212,175,55,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${isSelected ? "rgba(212,175,55,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                                <div className="w-12 h-12 rounded-xl bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${trip.image_url || 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=200'})` }} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm text-white truncate">{trip.destination}</div>
                                    <div className="text-[10px] text-white/50 uppercase tracking-widest mt-1">Trip</div>
                                </div>
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`/trip/${trip.id}`, '_blank');
                                    }}
                                    className="w-8 h-8 rounded-full grid place-items-center shrink-0 transition-colors hover:bg-white/10 text-white/50 hover:text-white"
                                    title="View Trip"
                                >
                                    <ArrowSquareOut size={16} />
                                </button>
                                <div className="w-5 h-5 rounded-full border grid place-items-center shrink-0 transition-colors" style={{ borderColor: isSelected ? "#D4AF37" : "rgba(255,255,255,0.2)", background: isSelected ? "#D4AF37" : "transparent" }}>
                                    {isSelected && <span className="text-[#06101c] text-xs leading-none mt-px">✓</span>}
                                </div>
                            </div>
                        );
                    }) : (
                        <div className="text-sm text-white/40 italic p-4">No recent trips found to select from.</div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(4,10,18,0.85)", backdropFilter: "blur(12px)" }} onClick={onClose} data-testid="concierge-recommendation-wizard">
            <motion.div initial={{ y: 20, opacity: 0, scale: 0.95 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()} className="max-w-xl w-full rounded-[32px] p-6 sm:p-8" style={{ background: "#07131f", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "#D4AF37" }}>AI Recommendations</div>
                        <div className="font-serif text-3xl text-white mt-2">Discover New Ideas</div>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 rounded-full grid place-items-center shrink-0 transition-colors hover:bg-white/10" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <X size={14} />
                    </button>
                </div>

                {isGenerating ? (
                    <div className="py-16 flex flex-col items-center justify-center text-center">
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} className="mb-6">
                            <Compass size={48} color="#D4AF37" weight="thin" />
                        </motion.div>
                        <div className="font-serif text-2xl text-white mb-2">Curating Your Destinations</div>
                        <p className="text-white/50 text-sm">Our AI is analyzing global options to find the perfect match...</p>
                    </div>
                ) : (
                    <>
                        {step === 1 ? renderStep1() : renderStep2()}
                        <div className="mt-8 pt-6 border-t flex justify-end gap-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                            {step === 2 && (
                                <button onClick={() => setStep(1)} className="px-5 py-2.5 rounded-full text-sm font-medium transition-colors hover:bg-white/10" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "white" }}>
                                    Back
                                </button>
                            )}
                            <button onClick={handleNext} disabled={!mode || (step === 2 && tripSelectionType === 'specific' && selectedTripIds.length === 0)} className="px-6 py-2.5 rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all" style={{ background: "linear-gradient(135deg, #D4AF37, #F0D57A)", color: "#06101c" }}>
                                {step === 1 && mode === "personalized" ? "Next Step" : "Generate Ideas"}
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
}
